import { useEffect } from "react";
import PopupDone, { usePopupDone } from "./PopupDone/PopupDone.jsx";

import "./SettingsFormModal.css";

/**
 * The dialog the /setting/* create and edit forms open in.
 *
 * Same shell ListAttendance puts AddAttendance in -- the `pg-modal`
 * veil, a `pg-modal-panel` with an eyebrow/title/subtitle head, a close
 * button, and the form itself in the body. Lifted out here because nine
 * settings pages need the identical thing and the alternative was nine
 * copies drifting apart.
 *
 * The form inside keeps its own submit and cancel buttons: it already had
 * them as a page, and a footer here would mean every form growing a way
 * to drive buttons it does not own.
 *
 * @param {boolean}  open       nothing renders when false
 * @param {string}   eyebrow    small caps line above the title
 * @param {string}   title      the dialog's heading
 * @param {string}   subtitle   one line under it saying what this is for
 * @param {string}   labelledBy id of the title element, for aria
 * @param {string}   size       "" | "wide" | "xl"
 * @param {Function} onClose    veil click, close button, and Escape
 */
const SettingsFormModal = ({
  open,
  eyebrow,
  title,
  subtitle,
  labelledBy = "settings-form-title",
  size = "",
  onClose,
  children,
}) => {
  // A save inside replaces the form with the done panel, which closes the
  // dialog after five seconds (PopupDone).
  const { done } = usePopupDone(Boolean(open));

  // Escape closes it. Bound on the document rather than the panel because
  // focus is usually inside a field, and a keydown there does not reach a
  // handler on the panel unless it bubbles through every input in between.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // The page behind must not scroll while the dialog is over it -- a wheel
  // over the veil otherwise moves the list underneath, which reads as the
  // dialog sliding.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  const sizeClass = size ? ` pg-modal-panel--${size}` : "";

  return (
    <div
      /* --over: a view popup may still be open behind this form (its Edit
         button opened it), and the form is the one on top. */
      className="pg-modal pg-modal--over"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onClick={onClose}
    >
      <div
        className={`pg-modal-panel${sizeClass} pg-modal-panel--settings`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            {eyebrow && <span className="pg-modal-eyebrow">{eyebrow}</span>}
            <h3 className="pg-modal-title" id={labelledBy}>
              {title}
            </h3>
            {subtitle && <p className="pg-modal-sub">{subtitle}</p>}
          </div>
          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? <PopupDone message={done} onClose={onClose} /> : children}
        </div>
      </div>
    </div>
  );
};

export default SettingsFormModal;
